import React, {FC, useCallback, useEffect} from "react";
import styled from "@emotion/styled";
import {IconButton} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {getIsOpenPlayerPool} from "modules/selectors";
import {setIsOpenPlayerPool} from "modules/actions";
import {useIsMobile} from "modules/utils/hooks";
import {IPlayer} from "modules/types";
import {PlayerPoolFilters, PlayerPoolList} from "components";
import {ReactComponent as CloseIcon} from "assets/images/icons/closeWhite.svg";
import {breakpoints} from "assets/theme";

const Wrapper = styled.div<{isOpen: boolean}>`
	display: flex;
	flex-direction: column;
	max-width: 425px;
	width: 100%;
	border-radius: 8px 8px 0 0;

	@media (max-width: 999px) {
		background: #cccccc;
		max-width: none;
		box-shadow: none;
		z-index: 502; // over all without error modal
		display: ${({isOpen}) => (isOpen ? "flex" : "none")};
		position: fixed;
		left: 0;
		top: 0;
		right: 0;
		--defaultPaddingBottom: 0px;
		bottom: var(--defaultPaddingBottom);
		/* iOS Safari 11.2, Safari 11 */
		bottom: calc(
			var(--defaultPaddingBottom) +
				constant(safe-area-inset-bottom, var(--defaultPaddingBottom))
		);
		/* iOS Safari 11.4+, Safari 11.1+, Chrome 69+, Opera 56+ */
		bottom: calc(
			var(--defaultPaddingBottom) + env(safe-area-inset-bottom, var(--defaultPaddingBottom))
		);
	}
`;

const Head = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 6px 16px 6px 24px;
	color: ${({theme}) => theme.colorPalette.white};
	background: ${({theme}) => theme.colorPalette.primary};
	width: 100%;
	min-height: 56px;

	svg {
		width: 28px;
		height: 28px;
	}

	@media (min-width: ${breakpoints.medium}) {
		border-radius: 8px 8px 0 0;
	}
`;

const Title = styled.div`
	font-weight: 700;
	font-size: 20px;
	line-height: 120%;
	letter-spacing: 0.2px;
`;

const Buttons = styled.div`
	display: none;

	@media (max-width: 999px) {
		display: flex;
	}
`;

interface IPlayerPool {
	PlayerAction: React.FC<{player: IPlayer; isDisabled: boolean}>;
	isDisabled: boolean;
}

export const PlayerPool: FC<IPlayerPool> = ({PlayerAction, isDisabled}) => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const {isMobile} = useIsMobile("(max-width: 1000px)");
	const isOpen = useSelector(getIsOpenPlayerPool);

	useEffect(() => {
		if (isOpen && isMobile) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "visible";
		}
	}, [isOpen, isMobile]);

	useEffect(() => {
		dispatch(setIsOpenPlayerPool(!isMobile));
	}, [dispatch, isMobile]);

	const closeHandler = useCallback(() => {
		dispatch(setIsOpenPlayerPool(false));
	}, [dispatch]);

	if (isMobile && !isOpen) {
		return null;
	}

	return (
		<Wrapper isOpen={isOpen}>
			<Head>
				<Title>{t("player_pool.header.title")}</Title>

				<Buttons>
					<IconButton onClick={closeHandler}>
						<CloseIcon />
					</IconButton>
				</Buttons>
			</Head>

			<PlayerPoolFilters />
			<PlayerPoolList PlayerAction={PlayerAction} isDisabled={isDisabled} />
		</Wrapper>
	);
};
